'use client'

import { Mic, Video } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { CHARACTER_THEMES } from '@/lib/character-themes'
import type { PublicChatAppLabels, SignupPromptReason } from '@/lib/free-santa'
import { cn } from '@/lib/utils'
import { SignupLink } from './signup-link'

interface SignupPromptProps {
  open: boolean
  /** Lo último que se pidió: se conserva al cerrar para que el texto no desaparezca durante el fundido. */
  reason: SignupPromptReason
  labels: PublicChatAppLabels['signupPrompt']
  signupHref: string
  onClose: () => void
}

/** Lo que cambia por motivo además de la copy: el icono y cómo se cuenta el clic. */
const BY_REASON = {
  voice: { icon: Mic, placement: 'voice_prompt', cta: 'signup' },
  call: { icon: Video, placement: 'call_prompt', cta: 'call' },
} as const

/**
 * El micrófono y la llamada del chat público: en vez de grabar o llamar, invitan
 * a crear la cuenta gratis, donde sí existen. «Seguir escribiendo» (o Escape,
 * o tocar fuera) vuelve al chat sin perder nada.
 */
export function SignupPrompt({ open, reason, labels, signupHref, onClose }: SignupPromptProps) {
  const { icon: Icon, placement, cta } = BY_REASON[reason]
  const copy = labels[reason]
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent showCloseButton={false} className="readable-body rounded-2xl bg-card p-6 text-card-foreground sm:max-w-md sm:p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary/15">
            <Icon className="size-8 text-primary" aria-hidden />
          </div>
          <DialogTitle className="font-christmas mb-3 text-2xl leading-tight font-bold">{copy.title}</DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">{copy.body}</DialogDescription>
        </div>
        <div className="flex flex-col gap-3">
          <SignupLink
            href={signupHref}
            placement={placement}
            cta={cta}
            className={cn(
              'flex min-h-14 w-full items-center justify-center rounded-2xl px-4 py-3 text-lg font-semibold',
              CHARACTER_THEMES.santa.videoCall.ctaButton
            )}
          >
            {labels.signup}
          </SignupLink>
          <Button type="button" variant="ghost" onClick={onClose} className="w-full">
            {labels.keepChatting}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
